import { useState } from 'react';
import { Copy, Languages, Trash2 } from 'lucide-react';
import { classNames } from '@/lib/utils';
import {
  IDIOMAS, NOMBRE_IDIOMA, copiarVersion, versionEmpezada, versionVacia,
  type AvisoVersion, type Campana, type CampanaEditable, type Idioma, type TipoDestinatario, type Version,
} from '@/lib/comunicaciones';
import EditorBloques from './EditorBloques';
import VistaPrevia from './VistaPrevia';

/**
 * Paso 2: el correo, una versión por idioma. Cada destinatario recibe la de su
 * idioma si está empezada y, si no, la de reserva. Una versión nueva puede
 * arrancar como copia de otra para traducirla encima.
 */
export default function PasoContenido({ campana, onChange }: {
  campana: Campana;
  onChange: (cambios: Partial<CampanaEditable>) => void;
}) {
  const [idioma, setIdioma] = useState<Idioma>(campana.idiomaReserva);
  const [avisos, setAvisos] = useState<Partial<Record<Idioma, AvisoVersion[]>>>({});
  const ambos = campana.audiencia.suscriptores.incluir && campana.audiencia.propietarios.incluir;
  const [verComo, setVerComo] = useState<TipoDestinatario>(campana.audiencia.suscriptores.incluir ? 'suscriptor' : 'propietario');

  const version = campana.versiones[idioma];
  const empezada = versionEmpezada(version);

  const cambiarVersion = (v: Version) => onChange({ versiones: { ...campana.versiones, [idioma]: v } });
  const empezarDesde = (origen?: Idioma) =>
    cambiarVersion(origen && campana.versiones[origen] ? copiarVersion(campana.versiones[origen]!) : versionVacia());
  const quitarVersion = () => {
    if (!window.confirm(`¿Quitar la versión en ${NOMBRE_IDIOMA[idioma].toLowerCase()}? Quien hable ${NOMBRE_IDIOMA[idioma].toLowerCase()} recibirá la de reserva.`)) return;
    const { [idioma]: _fuera, ...resto } = campana.versiones;
    onChange({ versiones: resto });
  };
  const empezadas = IDIOMAS.filter(i => versionEmpezada(campana.versiones[i]));

  return (
    <div className="space-y-4">
      {/* Idiomas */}
      <div className="card p-3 flex flex-wrap items-center gap-2">
        <Languages size={16} className="text-sereno-dark/40 ml-1" />
        {IDIOMAS.map(i => {
          const lista = versionEmpezada(campana.versiones[i]);
          const errores = (avisos[i] || []).some(a => a.nivel === 'error');
          return (
            <button
              key={i}
              type="button"
              onClick={() => setIdioma(i)}
              className={classNames(
                'flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border',
                i === idioma ? 'bg-sereno-dark text-white border-sereno-dark' : 'border-gray-200 text-sereno-dark/60 hover:border-slate-300',
              )}
            >
              <span className={classNames('w-1.5 h-1.5 rounded-full', !lista ? 'bg-slate-300' : errores ? 'bg-red-500' : 'bg-emerald-500')} />
              {NOMBRE_IDIOMA[i]}
              {campana.idiomaReserva === i && <span className={classNames('text-[10px] uppercase tracking-wide', i === idioma ? 'text-white/70' : 'text-sereno-dark/40')}>reserva</span>}
            </button>
          );
        })}
        <label className="ml-auto flex items-center gap-2 text-xs text-sereno-dark/50">
          Si no hay versión en su idioma, recibe la de
          <select
            className="input py-1 text-xs w-auto"
            value={campana.idiomaReserva}
            onChange={e => onChange({ idiomaReserva: e.target.value as Idioma })}
          >
            {IDIOMAS.map(i => <option key={i} value={i}>{NOMBRE_IDIOMA[i]}</option>)}
          </select>
        </label>
      </div>

      {!empezada ? (
        <div className="card text-center py-10">
          <p className="text-sereno-dark/60 mb-1">No hay versión en {NOMBRE_IDIOMA[idioma].toLowerCase()}.</p>
          <p className="text-sm text-sereno-dark/40 mb-5">
            {idioma === campana.idiomaReserva
              ? 'Es el idioma de reserva: la reciben todos los que no tengan la suya, así que hace falta.'
              : `Quien hable ${NOMBRE_IDIOMA[idioma].toLowerCase()} recibirá la versión en ${NOMBRE_IDIOMA[campana.idiomaReserva].toLowerCase()}.`}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {empezadas.map(o => (
              <button key={o} type="button" onClick={() => empezarDesde(o)} className="btn-ghost border border-gray-200 text-sm flex items-center gap-1.5">
                <Copy size={14} /> Copiar la de {NOMBRE_IDIOMA[o].toLowerCase()} para traducirla
              </button>
            ))}
            <button type="button" onClick={() => empezarDesde()} className="btn-primary text-sm">Empezar en blanco</button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
          <div className="space-y-4">
            <div className="card space-y-3">
              <div>
                <label className="label">Asunto</label>
                <input className="input" value={version!.asunto} maxLength={200} placeholder="Lo que se lee en la bandeja" onChange={e => cambiarVersion({ ...version!, asunto: e.target.value })} />
                <div className="text-[11px] text-sereno-dark/35 mt-1">{version!.asunto.length} caracteres · en el móvil se ven unos 40</div>
              </div>
              <div>
                <label className="label">Preheader</label>
                <input className="input" value={version!.preheader} maxLength={200} placeholder="La línea gris que acompaña al asunto en la bandeja" onChange={e => cambiarVersion({ ...version!, preheader: e.target.value })} />
              </div>
            </div>
            <EditorBloques bloques={version!.bloques} onChange={bloques => cambiarVersion({ ...version!, bloques })} />
            {idioma !== campana.idiomaReserva && (
              <button type="button" onClick={quitarVersion} className="text-xs text-sereno-dark/40 hover:text-red-600 flex items-center gap-1">
                <Trash2 size={13} /> Quitar la versión en {NOMBRE_IDIOMA[idioma].toLowerCase()}
              </button>
            )}
          </div>
          <div className="xl:sticky xl:top-4 space-y-2">
            {ambos && (
              <div className="flex items-center gap-2 text-xs text-sereno-dark/50">
                Ver como
                <select className="input py-1 text-xs w-auto" value={verComo} onChange={e => setVerComo(e.target.value as TipoDestinatario)}>
                  <option value="suscriptor">suscriptor</option>
                  <option value="propietario">propietario</option>
                </select>
                <span className="text-sereno-dark/35">(cambia el pie del correo)</span>
              </div>
            )}
            <VistaPrevia
              version={version!}
              idioma={idioma}
              tipo={ambos ? verComo : campana.audiencia.suscriptores.incluir ? 'suscriptor' : 'propietario'}
              remitente={campana.remitente.direccion}
              onAvisos={a => setAvisos(prev => ({ ...prev, [idioma]: a }))}
            />
          </div>
        </div>
      )}
    </div>
  );
}
